/**
 * 패널 맵 선택 로직 — OpenLayers Select interaction 대체 (순수 함수, 테스트 가능)
 */
import { readStringSeq } from './panelMapFeatureProps.js';

export const PANEL_MAP_SELECTION_MODE = {
  REPLACE: 'replace',
  TOGGLE: 'toggle'
};

/** Shift/Ctrl/Cmd — String 그룹(String+Module) 추가·제거 (다중 선택) */
export function isMapAdditiveSelectionEvent(event) {
  const originalEvent = event?.originalEvent ?? event;
  return Boolean(originalEvent?.shiftKey || originalEvent?.ctrlKey || originalEvent?.metaKey);
}

/** Ctrl/Cmd 누른 채 드래그 — Translate(이동) 대신 Ctrl+드래그 복사 */
export function isMapTranslateBlockedEvent(event) {
  const originalEvent = event?.originalEvent ?? event;
  return Boolean(originalEvent?.ctrlKey || originalEvent?.metaKey);
}

/** @param {import('ol').Feature|{ get: Function }} feature */
export function getFeatureType(feature) {
  if (!feature?.get) {
    return '';
  }
  return (feature.get('featureType') || '').toLowerCase();
}

/** @param {import('ol').Feature|{ get: Function }} feature */
export function isGroupSelectableFeature(feature) {
  if (!feature?.get) {
    return false;
  }
  const name = feature.get('name') || '';
  return name.startsWith('String') || name.startsWith('Module');
}

/** @param {import('ol').Feature|{ get: Function }} feature */
export function isModuleOnlySelectableFeature(feature) {
  if (!feature?.get) {
    return false;
  }
  const name = feature.get('name') || '';
  return name.startsWith('Module');
}

/**
 * Module 클릭 시 부모 String 피처 반환
 * @param {import('ol').Feature} hitFeature
 * @param {import('ol').Feature[]} allFeatures
 */
export function findParentStringFeature(hitFeature, allFeatures) {
  if (!hitFeature || !allFeatures?.length) {
    return null;
  }
  if (getFeatureType(hitFeature) !== 'module') {
    return null;
  }
  const stringSeq = resolveStringSeqFromMapFeature(hitFeature);
  if (!stringSeq) {
    return null;
  }
  return (
    allFeatures.find(
      f => getFeatureType(f) === 'string' && resolveStringSeqFromMapFeature(f) === stringSeq
    ) || null
  );
}

/**
 * 그룹 선택 대상(String)으로 승격
 * @param {import('ol').Feature} hitFeature
 * @param {import('ol').Feature[]} allFeatures
 */
export function resolveGroupSelectionTarget(hitFeature, allFeatures) {
  if (!hitFeature) {
    return null;
  }
  const ft = getFeatureType(hitFeature);
  if (ft === 'module') {
    return findParentStringFeature(hitFeature, allFeatures) || hitFeature;
  }
  if (ft === 'string') {
    return hitFeature;
  }
  return null;
}

/**
 * @param {import('ol').Feature[]} previousFeatures
 * @param {import('ol').Feature[]} nextFeatures
 */
export function computeSelectionDiff(previousFeatures, nextFeatures) {
  const prev = new Set(previousFeatures);
  const nextSet = new Set(nextFeatures);
  const selected = nextFeatures.filter(f => !prev.has(f));
  const deselected = previousFeatures.filter(f => !nextSet.has(f));
  return { selected, deselected };
}

/**
 * @param {import('ol').Feature[]} currentFeatures
 * @param {import('ol').Feature[]} toggleFeatures
 */
export function applyToggleSelection(currentFeatures, toggleFeatures) {
  const current = [...currentFeatures];
  const set = new Set(current);
  for (const feature of toggleFeatures) {
    if (set.has(feature)) {
      const idx = current.indexOf(feature);
      if (idx >= 0) {
        current.splice(idx, 1);
      }
      set.delete(feature);
    } else {
      current.push(feature);
      set.add(feature);
    }
  }
  return current;
}

/**
 * 싱글클릭 그룹 선택 (Select set-mode + Module→String 승격)
 * @param {Object} params
 * @param {import('ol').Feature|null} params.hitFeature
 * @param {import('ol').Feature[]} params.allFeatures
 * @param {(stringSeq: string) => import('ol').Feature[]} [params.relatedFeaturesForStringSeq]
 * @param {import('ol').Feature[]} params.currentSelection
 * @param {'replace'|'toggle'} [params.mode]
 */
export function resolveGroupClickSelection({
  hitFeature,
  allFeatures,
  relatedFeaturesForStringSeq,
  currentSelection,
  mode = PANEL_MAP_SELECTION_MODE.REPLACE
}) {
  const current = currentSelection || [];

  if (!hitFeature || !isGroupSelectableFeature(hitFeature)) {
    return {
      nextSelection: [],
      selected: [],
      deselected: current,
      clear: true,
      primaryTarget: null
    };
  }

  const primaryTarget = resolveGroupSelectionTarget(hitFeature, allFeatures);
  if (!primaryTarget) {
    return {
      nextSelection: [],
      selected: [],
      deselected: current,
      clear: true,
      primaryTarget: null
    };
  }

  const stringSeq = resolveStringSeqFromMapFeature(primaryTarget);
  const related =
    stringSeq && relatedFeaturesForStringSeq
      ? relatedFeaturesForStringSeq(stringSeq)
      : [primaryTarget];

  const nextSelection =
    mode === PANEL_MAP_SELECTION_MODE.TOGGLE
      ? applyToggleSelection(current, related)
      : [...related];

  const { selected, deselected } = computeSelectionDiff(current, nextSelection);

  return {
    nextSelection,
    selected,
    deselected,
    clear: nextSelection.length === 0,
    primaryTarget
  };
}

/**
 * 더블클릭 모듈 단독 선택 (Select set-mode)
 * @param {import('ol').Feature|null} hitFeature
 * @param {import('ol').Feature[]} currentModuleSelection
 */
export function resolveModuleDblClickSelection(hitFeature, currentModuleSelection = []) {
  const current = currentModuleSelection || [];

  if (!hitFeature || !isModuleOnlySelectableFeature(hitFeature)) {
    return {
      nextSelection: [],
      selected: [],
      deselected: current,
      clear: true
    };
  }

  const nextSelection = [hitFeature];
  const { selected, deselected } = computeSelectionDiff(current, nextSelection);

  return {
    nextSelection,
    selected,
    deselected,
    clear: false
  };
}

/**
 * Translate 시작 시 복원용 geometry 스냅샷 (String 이동 시 동반 Module 포함)
 * @param {import('ol').Feature[]} translateFeatureList
 * @param {{ getFeatures?: () => import('ol').Feature[] }} [vectorSource]
 */
export function captureTranslateGeometrySnapshots(translateFeatureList, vectorSource) {
  const snapshots = new Map();
  const add = feature => {
    const geom = feature?.getGeometry?.();
    if (geom && !snapshots.has(feature)) {
      snapshots.set(feature, geom.clone());
    }
  };

  (translateFeatureList || []).forEach(add);

  (translateFeatureList || []).forEach(feature => {
    if (getFeatureType(feature) !== 'string') return;
    const stringSeq = readStringSeq(feature);
    if (stringSeq == null || !vectorSource?.getFeatures) return;
    vectorSource.getFeatures().forEach(candidate => {
      if (getFeatureType(candidate) !== 'module') return;
      if (readStringSeq(candidate) === stringSeq) {
        add(candidate);
      }
    });
  });

  return snapshots;
}

/**
 * Modify/Translate 대상 분리 (PanelPlanningPage.updateModifyTranslateFeatures 동일)
 * @param {import('ol').Feature[]} groupFeatures
 * @param {import('ol').Feature[]} [moduleFeatures]
 */
export function buildTranslateFeaturesFromSelection(groupFeatures, moduleFeatures = []) {
  const translateFeatures = [];
  const selectedStringSeqs = new Set();

  for (const feature of groupFeatures) {
    if (getFeatureType(feature) === 'string') {
      const stringSeq = resolveStringSeqFromMapFeature(feature);
      if (stringSeq) {
        selectedStringSeqs.add(stringSeq);
        translateFeatures.push(feature);
      }
    }
  }

  for (const feature of groupFeatures) {
    if (getFeatureType(feature) === 'module') {
      const moduleStringSeq = resolveStringSeqFromMapFeature(feature);
      if (!moduleStringSeq || !selectedStringSeqs.has(moduleStringSeq)) {
        translateFeatures.push(feature);
      }
    }
  }

  for (const feature of moduleFeatures) {
    if (!translateFeatures.includes(feature)) {
      translateFeatures.push(feature);
    }
  }

  return translateFeatures;
}

/**
 * OL Collection diff (setPanelMapSelection)
 * @param {import('ol/Collection').default} collection
 * @param {import('ol').Feature[]} targetFeatures
 */
/**
 * 클릭 픽셀에 겹친 피처 중 실제 선택 대상(하이라이트 제외) 반환
 * @param {import('ol').Feature[]} featuresAtPixel
 */
export function pickMapClickTargetFeature(featuresAtPixel) {
  if (!featuresAtPixel?.length) {
    return null;
  }
  const candidates = featuresAtPixel.filter(
    f => f?.get && !f.get('_isSelectionHighlight')
  );
  if (!candidates.length) {
    return null;
  }
  // String은 논리 그룹 — 클릭·선택은 Module(또는 Array)만 대상
  const module = candidates.find(f => getFeatureType(f) === 'module');
  if (module) {
    return module;
  }
  const nonString = candidates.find(f => getFeatureType(f) !== 'string');
  return nonString ?? null;
}

export function diffOlFeatureCollection(collection, targetFeatures) {
  const related = targetFeatures || [];
  const targetSet = new Set(related);
  const toRemove = [];

  collection.forEach(feature => {
    if (!targetSet.has(feature)) {
      toRemove.push(feature);
    }
  });
  toRemove.forEach(feature => collection.remove(feature));

  const existing = new Set(collection.getArray());
  related.forEach(feature => {
    if (!existing.has(feature)) {
      collection.push(feature);
    }
  });
}

/** @param {import('ol').Feature|{ get: Function }} feature */
export function resolveStringSeqFromMapFeature(feature) {
  return readStringSeq(feature);
}

/**
 * sparse groupSelection 앵커들에서 고유 stringSeq 목록
 * @param {import('ol').Feature[]} features
 */
export function getUniqueStringSeqsFromSelectionFeatures(features) {
  const seqs = new Set();
  (features || []).forEach(feature => {
    const seq = resolveStringSeqFromMapFeature(feature);
    if (seq != null) {
      seqs.add(seq);
    }
  });
  return [...seqs];
}

/**
 * 맵 우클릭 컨텍스트 메뉴 — 삭제 항목 정책
 *
 * Module 삭제 (`resolveMapContextMenuModuleDeleteTarget`)
 *   - Module 더블클릭 단독 선택(moduleSelection만, groupSelection 없음)일 때만
 *   - 우클릭 위치와 무관 — 선택된 Module 삭제
 *
 * String 삭제 (`resolveMapContextMenuStringDeleteTargets`) — 평소 기본
 *   - Module-only 선택 중이면 String 삭제 숨김
 *   - 다중 String 그룹 선택 → 선택된 String들 삭제
 *   - 그 외: 우클릭 Module→부모 String, String hit→해당 String
 *   - 폴백: 단일 groupSelection String
 *
 * Module-only 선택 시 Module 삭제만, 그 외에는 String 삭제 우선.
 */

/**
 * 맵 우클릭 메뉴 — 삭제 대상 stringSeq resolve
 * 다중 선택(Shift 등) > 우클릭 위치 피처 > 단일 선택 순
 *
 * @param {object} p
 * @param {import('ol').Feature|null} p.hitFeature
 * @param {import('ol').Feature[]} p.groupSelectionFeatures
 * @param {import('ol').Feature[]} p.allFeatures
 * @param {boolean} p.moduleOnlySelected
 */
export function resolveMapContextMenuStringDeleteTargets({
  hitFeature,
  groupSelectionFeatures,
  allFeatures,
  moduleOnlySelected
}) {
  if (moduleOnlySelected) {
    return null;
  }

  const selectedSeqs = getUniqueStringSeqsFromSelectionFeatures(groupSelectionFeatures);
  if (selectedSeqs.length > 1) {
    return { mode: 'multi', stringSeqs: selectedSeqs };
  }

  const anchor = resolveGroupSelectionTarget(hitFeature, allFeatures);
  const hitSeq = resolveStringSeqFromMapFeature(anchor);
  if (hitSeq != null) {
    return { mode: 'single', stringSeqs: [hitSeq] };
  }

  if (selectedSeqs.length === 1) {
    return { mode: 'single', stringSeqs: selectedSeqs };
  }

  return null;
}

/**
 * 맵 우클릭 — Module 삭제 대상
 * Module 더블클릭 단독 선택(moduleSelection만)일 때만.
 */
export function resolveMapContextMenuModuleDeleteTarget({
  hitFeature,
  moduleSelectionFeatures,
  groupSelectionFeatures
}) {
  void hitFeature;
  const groupCount = groupSelectionFeatures?.length ?? 0;
  const moduleCount = moduleSelectionFeatures?.length ?? 0;

  if (moduleCount > 0 && groupCount === 0) {
    return { feature: moduleSelectionFeatures[0] };
  }
  return null;
}
